import { useToast, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useMemo } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { WebChatBotAccountContext } from "./WebChatBotAccountContext";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { extractNonEmptyAccount } from "../../utils/extractNonEmptyAccount";

export const WebChatBotAccountProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { toast } = useToast();
  const { t } = useTranslations();

  const { crmConfig } = useManageAccountsDrawer();

  const [getChatBotToken, { isLoading: isGetChatBotTokenLoading }] =
    integrationsApi.useGetChatBotTokenMutation();

  const [
    updateWebChatBotConfig,
    { isLoading: isUpdateWebChatBotConfigLoading },
  ] = integrationsApi.useUpdateWebChatBotConfigMutation();

  const webChatBotAccount = useMemo(
    () => extractNonEmptyAccount(crmConfig?.chatbot),
    [crmConfig?.chatbot]
  );

  const isConnected = useMemo(() => {
    // We consider the web chat bot always enabled for now
    // return !!webChatBotAccount?.enabled
    return true;
  }, []);

  const connectWebChatBotAccount = useCallback(async () => {
    const response = await getChatBotToken({ entityId });

    if (response && !("data" in response)) {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
      throw new Error();
    }
  }, [entityId, getChatBotToken, t, toast]);

  const toggleWebChatBotAccount = useCallback(async () => {
    if (!webChatBotAccount) return;

    const resp = await updateWebChatBotConfig({
      entityId,
      enabled: !webChatBotAccount.enabled,
    });

    if (resp && "data" in resp) {
      if (resp.data.enabled) {
        toast(
          t({
            id: "client-suppoty-page.web-live-chat-is-enabled",
            defaultMessage: "Web live chat is enabled",
          })
        );
      } else {
        toast(
          t({
            id: "client-suppoty-page.web-live-chat-is-disabled",
            defaultMessage: "Web live chat is disabled",
          })
        );
      }
      return;
    }

    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  }, [entityId, t, toast, updateWebChatBotConfig, webChatBotAccount]);

  const contextValue = useMemo(
    () => ({
      webChatBotAccount,
      isConnected,
      isConnectingWebChatBotAccount: isGetChatBotTokenLoading,
      connectWebChatBotAccount,
      toggleWebChatBotAccount,
      isToggleWebChatBotAccountLoading: isUpdateWebChatBotConfigLoading,
    }),
    [
      webChatBotAccount,
      isConnected,
      isGetChatBotTokenLoading,
      connectWebChatBotAccount,
      toggleWebChatBotAccount,
      isUpdateWebChatBotConfigLoading,
    ]
  );

  return (
    <WebChatBotAccountContext.Provider value={contextValue}>
      {children}
    </WebChatBotAccountContext.Provider>
  );
};
